import React, { useState, useMemo } from 'react';

interface PieceItem {
  slug: string;
  title?: string;
  lifecycleState: string;
  updatedAt?: string;
  stages: Record<string, any>;
  renditions: Record<string, any>;
  publications: Record<string, any>;
  evalScore?: number | null;
  evalGrade?: string | null;
}

interface LibraryProps {
  pieces: PieceItem[];
  brand: string;
  onSelectPiece: (slug: string) => void;
  onNewPiece: () => void;
  onSuggestTopics?: () => void;
  /** Optional — hand the piece off to Social Studio with the slug pre-filled. */
  onCreateSocial?: (slug: string) => void;
}

// Volume/ebook status mapping moved to EbooksView — the Library is piece-only now.

// #228 (img3): a piece counts as "Published" the moment it has ANY recorded
// publication. The publish flow writes manifest.publications but does NOT advance
// lifecycleState, so deriving status from publications is the source of truth for
// "is this out the door". The badge AND the status filter both go through
// statusBucket() so they can never disagree — that divergence is what made the
// filter "leak" (a published piece kept its stale draft/review state and showed
// up under Writing/Review).
function isPublished(p: { publications?: Record<string, any> }): boolean {
  return !!p.publications && Object.keys(p.publications).length > 0;
}

/** Effective status bucket: a recorded publication wins; else map the lifecycle state. */
function statusBucket(p: { lifecycleState: string; publications?: Record<string, any> }): string {
  if (isPublished(p)) return 'published';
  switch (p.lifecycleState) {
    case 'spark': case 'research': case 'plan': return 'idea';
    case 'draft': return 'writing';
    case 'review': return 'review';
    case 'ready': return 'ready';
    case 'published': return 'published';
    case 'archived': return 'archived';
    default: return 'idea';
  }
}

// Badge per bucket — keyed by statusBucket()'s output so the bucket switch is the
// single source of truth for piece status (badge + filter + sort all derive from it).
const BUCKET_BADGE: Record<string, { label: string; className: string }> = {
  idea: { label: 'Idea', className: 'status-pill--idea' },
  writing: { label: 'Writing', className: 'status-pill--writing' },
  review: { label: 'Review', className: 'status-pill--review' },
  ready: { label: 'Ready', className: 'status-pill--ready' },
  published: { label: 'Published', className: 'status-pill--live' },
  archived: { label: 'Archived', className: 'status-pill--archived' },
};

/** Badge for a piece, via the publications-aware bucket. */
function pieceBadge(p: { lifecycleState: string; publications?: Record<string, any> }): { label: string; className: string } {
  return BUCKET_BADGE[statusBucket(p)] || { label: p.lifecycleState, className: '' };
}

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** Social Studio scratch pieces (carousels / posts created from a topic without
 *  a Library trip). Surfaced under the "Social drafts" filter so they're
 *  recoverable, but kept OUT of the default Library view so it stays clean. */
function isScratch(p: { slug: string }): boolean {
  return p.slug.startsWith('scratch--');
}

function bestScore(renditions: Record<string, any>): number | null {
  let worst: number | null = null;
  for (const r of Object.values(renditions || {})) {
    if (r.score != null) worst = worst === null ? r.score : Math.min(worst, r.score);
  }
  return worst;
}

function timeAgo(iso: string | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function Library({ pieces, brand, onSelectPiece, onNewPiece, onSuggestTopics, onCreateSocial }: LibraryProps) {
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'title' | 'status' | 'quality' | 'updated'>('updated');
  const [sortAsc, setSortAsc] = useState(false);

  const scratchCount = useMemo(() => pieces.filter(isScratch).length, [pieces]);

  const filteredPieces = useMemo(() => {
    // The "Social drafts" filter shows ONLY scratch (Social Studio) pieces;
    // every other view EXCLUDES them so the main Library stays uncluttered.
    let items = statusFilter === 'social'
      ? pieces.filter(isScratch)
      : pieces.filter((p) => !isScratch(p));
    if (statusFilter && statusFilter !== 'social') {
      // Same derivation as the badge — publications-aware — so the filter and the
      // displayed status always agree.
      items = items.filter((p) => statusBucket(p) === statusFilter);
    }

    items.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'title') cmp = (a.title || a.slug).localeCompare(b.title || b.slug);
      else if (sortField === 'quality') cmp = (bestScore(a.renditions) ?? -1) - (bestScore(b.renditions) ?? -1);
      else if (sortField === 'status') cmp = statusBucket(a).localeCompare(statusBucket(b));
      else cmp = (a.updatedAt || '').localeCompare(b.updatedAt || '');
      return sortAsc ? cmp : -cmp;
    });

    return items;
  }, [pieces, statusFilter, sortField, sortAsc]);

  if (pieces.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state__title">Start creating</div>
        <div className="empty-state__text">Give the engine a topic and watch it produce content.</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onNewPiece}>
          + New piece
        </button>
      </div>
    );
  }

  function handleSort(field: typeof sortField) {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(false); }
  }

  const statusFilters: Array<{ value: string | null; label: string }> = [
    { value: null, label: 'All' },
    { value: 'idea', label: 'Ideas' },
    { value: 'writing', label: 'Writing' },
    { value: 'review', label: 'Review' },
    { value: 'ready', label: 'Ready' },
    { value: 'published', label: 'Published' },
    // Social Studio drafts — only shown when some exist, so it's discoverable
    // exactly when there's something to recover.
    ...(scratchCount > 0 ? [{ value: 'social', label: `Social drafts (${scratchCount})` }] : []),
  ];

  return (
    <div className="library">
      {/* Toolbar */}
      <div className="library__toolbar">
        <div className="library__filters">
          {statusFilters.map((f) => (
            <button
              key={f.value ?? 'all'}
              className={`library__filter-btn ${statusFilter === f.value ? 'library__filter-btn--active' : ''}`}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="library__toolbar-right">
          <button
            className={`library__view-btn ${viewMode === 'table' ? 'library__view-btn--active' : ''}`}
            onClick={() => setViewMode('table')}
            title="Table view"
          >&#9776;</button>
          <button
            className={`library__view-btn ${viewMode === 'card' ? 'library__view-btn--active' : ''}`}
            onClick={() => setViewMode('card')}
            title="Card view"
          >&#9638;</button>
          {onSuggestTopics && (
            <button
              className="btn btn-secondary"
              onClick={onSuggestTopics}
              title="Suggest fresh topics from corpus gaps + recent threads"
              style={{ marginRight: 8 }}
            >
              {'\u{1F4A1}'} Suggest topics
            </button>
          )}
          <button className="btn btn-primary" onClick={onNewPiece}>+ New</button>
        </div>
      </div>

      {/* Table view */}
      {viewMode === 'table' && (
        <div className="library__table-wrap">
          <table className="library__table">
            <thead>
              <tr>
                <th className="library__th" onClick={() => handleSort('title')}>
                  Title {sortField === 'title' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th className="library__th" onClick={() => handleSort('status')}>Status</th>
                <th className="library__th" onClick={() => handleSort('quality')}>Quality</th>
                <th className="library__th">Formats</th>
                <th className="library__th" onClick={() => handleSort('updated')}>Updated</th>
                <th className="library__th"></th>
              </tr>
            </thead>
            <tbody>
              {filteredPieces.map((p) => {
                const score = p.evalScore ?? bestScore(p.renditions);
                const status = pieceBadge(p);
                const fmtCount = Object.keys(p.renditions || {}).length;
                return (
                  <tr key={p.slug} className="library__row" tabIndex={0} onClick={() => onSelectPiece(p.slug)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectPiece(p.slug); } }}>
                    <td className="library__td library__td--title">
                      {p.title || humanTitle(p.slug)}
                      {isScratch(p) && <span className="status-pill" style={{ marginLeft: 8, fontSize: 10, opacity: 0.75 }}>Social draft</span>}
                    </td>
                    <td className="library__td"><span className={`status-pill ${status.className}`}>{status.label}</span></td>
                    <td className="library__td">
                      {score !== null ? (
                        <span className={`library__score ${score >= 85 ? 'library__score--good' : score >= 70 ? 'library__score--ok' : 'library__score--bad'}`} title={p.evalScore != null ? 'Eval score (SEO+GEO+Virality avg)' : 'Render validation score'}>
                          {score}{p.evalScore != null ? '' : ''}
                        </span>
                      ) : <span className="library__score library__score--none">&mdash;</span>}
                    </td>
                    <td className="library__td library__td--dim">{fmtCount || '\u2014'}</td>
                    <td className="library__td library__td--dim">{timeAgo(p.updatedAt)}</td>
                    <td className="library__td" onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <a
                          href={`/eval/${brand}/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="eval-btn"
                          title="Open Eval Report"
                        >
                          Eval
                        </a>
                        {onCreateSocial && (
                          <button
                            type="button"
                            className="eval-btn"
                            data-testid="library-create-social"
                            onClick={() => onCreateSocial(p.slug)}
                            title="Generate a LinkedIn or X post from this piece"
                            style={{ background: 'transparent', border: '1px solid var(--line)', cursor: 'pointer' }}
                          >
                            Social
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

        </div>
      )}

      {/* Card view */}
      {viewMode === 'card' && (
        <div className="library__cards">
          {filteredPieces.map((p) => {
            const score = p.evalScore ?? bestScore(p.renditions);
            const status = pieceBadge(p);
            return (
              <div key={p.slug} className="library__card" role="button" tabIndex={0} aria-label={`Open ${p.title || p.slug}`} onClick={() => onSelectPiece(p.slug)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectPiece(p.slug); } }}>
                <div className="library__card-title">
                  {p.title || humanTitle(p.slug)}
                  {isScratch(p) && <span className="status-pill" style={{ marginLeft: 8, fontSize: 10, opacity: 0.75 }}>Social draft</span>}
                </div>
                <div className="library__card-meta">
                  <span className={`status-pill ${status.className}`}>{status.label}</span>
                  {score !== null && (
                    <span className={`library__score ${score >= 85 ? 'library__score--good' : score >= 70 ? 'library__score--ok' : 'library__score--bad'}`}>{score}</span>
                  )}
                </div>
                <div className="library__card-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{Object.keys(p.renditions || {}).length} formats &middot; {timeAgo(p.updatedAt)}</span>
                  <a
                    href={`/eval/${brand}/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="eval-btn"
                    onClick={(e) => e.stopPropagation()}
                    title="Open Eval Report"
                  >
                    Eval
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
